import Expander from 'components/utils/Expander'
import Group from 'components/utils/Group'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import Sheet from 'components/utils/Sheet'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Clickable from 'components/Common/Clickable'
import Divider from 'LuxKit/Divider'
import FormatCurrency from 'components/Common/FormatCurrency'
import getLoyaltyUpgradeTargetDisplayName from 'loyalty/lib/benefits/getLoyaltyUpgradeTargetDisplayName'
import { rem } from 'polished'
import type { PropsWithChildren } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { tierDarkForegroundColorStyle } from 'styles/utils/tierStyles'

const SectionHeader = styled(Clickable)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${rem(12)};
  width: 100%;
  padding: ${rem(20)};
  text-align: start;
`

const TierAccentText = styled(BodyText)`
  ${tierDarkForegroundColorStyle}
`

const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${rem(12)};
`

const SectionBody = styled.div`
  padding: 0 ${rem(20)} ${rem(20)};
`

interface Props extends PropsWithChildren {
  /** Which kind of upgrade this section offers. Drives the heading copy and how the name is shown. */
  upgradeType: App.LoyaltyStayUpgradeType
  /**
   * The target's raw name. A package name is trimmed to its upgrade descriptor — the room is already
   * named by the row this modal was opened from. A room name is already the bare room, so it is shown
   * as-is rather than split on any dash it happens to contain.
   */
  targetName: string | undefined
  isExpanded: boolean
  onToggle: () => void
  onApply: () => void
  /** True when this section's upgrade is the one currently applied, which offers Remove instead. */
  isApplied: boolean
  onRemove: () => void
  /** Drives the tier-coloured accent on the member-facing price line. */
  tier: App.LoyaltyTier
  /** What the upgraded package would normally cost — the struck-through figure conceptually. */
  standardPrice: number
  /** What the member actually pays once the free upgrade is applied: the source package's price. */
  yourPrice: number
}

/**
 * One collapsible upgrade offer inside the stay-upgrade modal. Sections toggle independently, so a
 * member can have both open at once.
 *
 * Deliberately takes plain values rather than an offer-page `packageView` or a checkout `itemView`:
 * both surfaces render this, and their shapes differ.
 */
function LoyaltyStayUpgradeAccordionSection({
  upgradeType,
  targetName,
  isExpanded,
  onToggle,
  onApply,
  isApplied,
  onRemove,
  tier,
  standardPrice,
  yourPrice,
  children,
}: Props) {
  const tierClassName = `tier-${tier}`
  const intl = useIntl()

  const displayName = upgradeType === 'package' ?
      getLoyaltyUpgradeTargetDisplayName(targetName) :
      (targetName?.trim() ?? '')

  const headingLabel = upgradeType === 'package' ?
      intl.formatMessage({
        id: '60n_56qwP7q3qc30RNYJ',
        defaultMessage: 'Upgrade your package to:',
      }) :
      intl.formatMessage({
        id: 'I5xTRVF9h4TFyvE2Mefg',
        defaultMessage: 'Upgrade your room to:',
      })

  const applyLabel = intl.formatMessage({
    id: '7IUk36wznQyavu1tycPr',
    defaultMessage: 'Apply Free Upgrade',
  })

  const removeLabel = intl.formatMessage({
    id: '_VS6e60u6y35ik9pnXvM',
    defaultMessage: 'Remove upgrade',
  })

  return <Sheet border="light-foreground" borderRadius="XL">
    <SectionHeader onClick={onToggle} aria-expanded={isExpanded}>
      <Group direction="vertical" gap={2}>
        <TierAccentText className={tierClassName} variant="medium" weight="bold">{headingLabel}</TierAccentText>
        <Heading variant="heading6">{displayName}</Heading>
      </Group>
      <DropdownChevron direction={isExpanded ? 'up' : 'down'} />
    </SectionHeader>
    <Expander expanded={isExpanded}>
      <SectionBody inert={!isExpanded}>
        <Group direction="vertical" gap={16}>
          {children}
          <Divider kind="primary" />

          <PriceRow>
            <BodyText variant="medium" weight="bold">
              {intl.formatMessage({
                id: 'OI4Hevo2SoLzBjrt0QTj',
                defaultMessage: 'Standard price for this package',
              })}
            </BodyText>
            <BodyText variant="medium">
              <FormattedMessage
                id="1nAjzdEJRwMgJugEo-uq"
                defaultMessage="From {amount} total"
                
                values={{ amount: <FormatCurrency value={standardPrice} format="roundedDollar" /> }}
              />
            </BodyText>
          </PriceRow>

          <Divider kind="primary" />

          <PriceRow>
            <TierAccentText className={tierClassName} variant="medium" weight="bold">
              {intl.formatMessage({
                id: '-cpUR-6aXBZ696wy_zoN',
                defaultMessage: 'Your price with a free upgrade',
              })}
            </TierAccentText>
            <TierAccentText className={tierClassName} variant="medium" weight="bold">
              <FormattedMessage
                id="1nAjzdEJRwMgJugEo-uq"
                defaultMessage="From {amount} total"
                
                values={{ amount: <FormatCurrency value={yourPrice} format="roundedDollar" /> }}
              />
            </TierAccentText>
          </PriceRow>

          <TextButton
            kind="primary"
            size="large"
            fit="full-width"
            onClick={isApplied ? onRemove : onApply}
          >
            {isApplied ? removeLabel : applyLabel}
          </TextButton>
        </Group>
      </SectionBody>
    </Expander>
  </Sheet>
}

export default LoyaltyStayUpgradeAccordionSection
